import { request, requestBlob } from './client'
import type {
  ChargeLookup,
  CreateChargebackInput,
  FraudAdminEventsResponse,
  FraudAdminUser,
  FraudPlatformResponse,
  FraudPlatformSettings,
  FraudUserProfile,
  CreateFraudAllowanceInput,
  CreateFraudBlockInput,
  DisputeDraft,
  DisputeRecommendation,
  DisputeReversal,
  FraudAllowance,
  FraudAssessmentsPage,
  FraudBlock,
  FraudChargeback,
  FraudOverview,
  FraudPolicyOverrides,
  FraudPolicyResponse,
  FraudReload,
  FraudCardDetail,
  FraudUserDetail,
  FraudUsersPage,
  StaffChargeback,
} from './fraud-types'

type Params = Record<string, string | number | boolean | undefined | null>

const eventBase = (eventId: number) => `/fraud/event/${eventId}`

export interface FraudUsersParams {
  limit?: number
  offset?: number
  min_score?: number
  /** Attendee name, email, phone or id. */
  q?: string
}

export interface FraudAssessmentsParams {
  since?: string
  decision?: string
  kind?: string
  outcome?: string
  user?: number
  limit?: number
}

/**
 * The parsed body of a failed request, when the client attached one
 * (`request` sets `data` on the thrown Error). Fraud endpoints answer
 * validation problems with `{field: [message]}` and refusals with
 * `{code, detail}`; `message` already folds either into readable text.
 */
export function apiErrorDetail(error: unknown, fallback: string): string {
  const data = (error as { data?: Record<string, unknown> })?.data
  if (data && typeof data.detail === 'string') return data.detail
  if (error instanceof Error && error.message) {
    // "403: text" → "text"
    return error.message.replace(/^\d{3}:\s*/, '')
  }
  return fallback
}

export const fraudClient = {
  getOverview: (token: string, eventId: number, params?: { window?: string; top?: number }): Promise<FraudOverview> =>
    request(`${eventBase(eventId)}/overview/`, { token, params: params as Params }),

  getUsers: (token: string, eventId: number, params?: FraudUsersParams): Promise<FraudUsersPage> =>
    request(`${eventBase(eventId)}/users/`, { token, params: params as Params }),

  getUser: (token: string, eventId: number, userId: number): Promise<FraudUserDetail> =>
    request(`${eventBase(eventId)}/users/${userId}/`, { token }),

  /** One physical card by any of its instrument ids: the accounts holding it here. */
  getCard: (token: string, eventId: number, instrumentId: string): Promise<FraudCardDetail> =>
    request(`${eventBase(eventId)}/cards/${encodeURIComponent(instrumentId)}/`, { token }),

  getAssessments: (token: string, eventId: number, params?: FraudAssessmentsParams): Promise<FraudAssessmentsPage> =>
    request(`${eventBase(eventId)}/assessments/`, { token, params: params as Params }),

  getPolicy: (token: string, eventId: number): Promise<FraudPolicyResponse> =>
    request(`${eventBase(eventId)}/policy/`, { token }),

  updatePolicy: (token: string, eventId: number, data: Partial<FraudPolicyOverrides>): Promise<FraudPolicyResponse> =>
    request(`${eventBase(eventId)}/policy/`, { method: 'PATCH', token, body: data }),

  getBlocks: (token: string, eventId: number, params?: { active?: boolean; user?: number }): Promise<FraudBlock[]> =>
    request(`${eventBase(eventId)}/blocks/`, { token, params: params as Params }),

  createBlock: (token: string, eventId: number, data: CreateFraudBlockInput): Promise<FraudBlock> =>
    request(`${eventBase(eventId)}/blocks/`, { method: 'POST', token, body: data }),

  liftBlock: (token: string, blockId: number, notes: string): Promise<FraudBlock> =>
    request(`/fraud/blocks/${blockId}/lift/`, { method: 'POST', token, body: { notes } }),

  getAllowances: (token: string, eventId: number, params?: { active?: boolean; user?: number }): Promise<FraudAllowance[]> =>
    request(`${eventBase(eventId)}/allowances/`, { token, params: params as Params }),

  createAllowance: (token: string, eventId: number, data: CreateFraudAllowanceInput): Promise<FraudAllowance> =>
    request(`${eventBase(eventId)}/allowances/`, { method: 'POST', token, body: data }),

  revokeAllowance: (token: string, allowanceId: number): Promise<FraudAllowance> =>
    request(`/fraud/allowances/${allowanceId}/revoke/`, { method: 'POST', token, body: {} }),

  /** `q` matches an attendee, a card's last four, a transaction or transfer id,
   *  or a payment-instrument id (every charge with that card). `limit` up to 200. */
  searchReloads: (token: string, eventId: number, q: string, limit?: number): Promise<FraudReload[]> =>
    request(`${eventBase(eventId)}/reloads/`, { token, params: { q: q || undefined, limit } }),

  lookupCharge: (token: string, eventId: number, params: { transaction?: number; transfer_id?: string }): Promise<ChargeLookup> =>
    request(`${eventBase(eventId)}/chargebacks/lookup/`, { token, params: params as Params, expectedErrorStatuses: [400] }),

  getChargebacks: (token: string, eventId: number, params?: { user?: number; state?: string }): Promise<FraudChargeback[]> =>
    request('/fraud/chargebacks/', { token, params: { event: eventId, ...params } as Params }),

  createChargeback: (token: string, data: CreateChargebackInput): Promise<FraudChargeback & { block: FraudBlock | null }> =>
    request('/fraud/chargebacks/', { method: 'POST', token, body: data }),

  updateChargeback: (token: string, chargebackId: number, data: Partial<FraudChargeback>): Promise<FraudChargeback> =>
    request(`/fraud/chargebacks/${chargebackId}/`, { method: 'PATCH', token, body: data }),

  // EventPay staff (Admin → Fraud). Every endpoint is superuser-only server-side.
  getPlatform: (token: string): Promise<FraudPlatformResponse> =>
    request('/fraud/platform/', { token }),

  updatePlatform: (token: string, data: Partial<Pick<FraudPlatformSettings, 'mode' | 'default_profile' | 'notes'>>): Promise<FraudPlatformResponse> =>
    request('/fraud/platform/', { method: 'PATCH', token, body: data }),

  getAdminEvents: (token: string, params?: { window?: string; scope?: 'active' | 'all'; q?: string }): Promise<FraudAdminEventsResponse> =>
    request('/fraud/admin/events/', { token, params: params as Params }),

  searchAdminUsers: (token: string, q: string): Promise<FraudAdminUser[]> =>
    request('/fraud/admin/users/', { token, params: { q } }),

  getUserProfile: (token: string, userId: number): Promise<FraudUserProfile> =>
    request(`/fraud/users/${userId}/`, { token }),

  getAllBlocks: (token: string, params?: { active?: boolean; user?: number }): Promise<FraudBlock[]> =>
    request('/fraud/blocks/', { token, params: params as Params }),

  createGlobalBlock: (token: string, data: CreateFraudBlockInput): Promise<FraudBlock> =>
    request('/fraud/blocks/', { method: 'POST', token, body: data }),

  getAllChargebacks: (token: string, params?: { state?: string; user?: number }): Promise<StaffChargeback[]> =>
    request('/fraud/chargebacks/', { token, params: params as Params }),

  // Drafted responses to Finix disputes. Submit and accept are one-shot at Finix.
  getDispute: (token: string, chargebackId: number): Promise<DisputeDraft> =>
    request(`/fraud/admin/disputes/${chargebackId}/`, { token }),

  updateDispute: (token: string, chargebackId: number, data: { response_note?: string; recommendation?: DisputeRecommendation }): Promise<DisputeDraft> =>
    request(`/fraud/admin/disputes/${chargebackId}/`, { method: 'PATCH', token, body: data }),

  /** Re-gather the facts and redraw the packet (refused once it was uploaded). */
  redraftDispute: (token: string, chargebackId: number): Promise<DisputeDraft> =>
    request(`/fraud/admin/disputes/${chargebackId}/draft/`, { method: 'POST', token, body: {}, expectedErrorStatuses: [400] }),

  /** 409 `evidence_processing`: Finix is still processing the upload; nothing was submitted, retry in a few minutes. */
  submitDispute: (token: string, chargebackId: number, data: { note?: string; override?: boolean }): Promise<DisputeDraft> =>
    request(`/fraud/admin/disputes/${chargebackId}/submit/`, { method: 'POST', token, body: data, expectedErrorStatuses: [400, 409] }),

  acceptDispute: (token: string, chargebackId: number, data: { note: string; override?: boolean }): Promise<DisputeDraft & { reversal: DisputeReversal }> =>
    request(`/fraud/admin/disputes/${chargebackId}/accept/`, { method: 'POST', token, body: data, expectedErrorStatuses: [400] }),

  getDisputeEvidence: (token: string, chargebackId: number): Promise<Blob> =>
    requestBlob(`/fraud/admin/disputes/${chargebackId}/evidence.pdf`, token),
}
